import type { QueryClient, QueryKey } from "@tanstack/react-query";
import { queryKeys } from "./query-keys";

type InvalidationTarget = {
  queryKey: QueryKey;
  exact?: boolean;
};

async function invalidateTargets(
  queryClient: QueryClient,
  targets: InvalidationTarget[],
) {
  await Promise.all(
    targets.map((target) =>
      queryClient.invalidateQueries({
        queryKey: target.queryKey,
        exact: target.exact,
      }),
    ),
  );
}

export async function invalidateAdminSession(queryClient: QueryClient) {
  await invalidateTargets(queryClient, [
    { queryKey: queryKeys.admin.session(), exact: true },
    { queryKey: queryKeys.admin.profile(), exact: true },
  ]);
}

export async function invalidateAdminAdmins(queryClient: QueryClient) {
  await invalidateTargets(queryClient, [
    { queryKey: queryKeys.admin.admins() },
  ]);
}

export async function invalidateAdminAppConfigs(queryClient: QueryClient) {
  await invalidateTargets(queryClient, [
    { queryKey: queryKeys.admin.appConfigs() },
  ]);
}

export async function invalidateAdminFeatureFlags(queryClient: QueryClient) {
  await invalidateTargets(queryClient, [
    { queryKey: queryKeys.admin.featureFlags() },
  ]);
}

export async function invalidateAdminFeatureFlagAudit(
  queryClient: QueryClient,
  id: string,
) {
  await invalidateTargets(queryClient, [
    { queryKey: queryKeys.admin.featureFlagAudit(id) },
  ]);
}

export async function invalidateAdminAreas(queryClient: QueryClient) {
  await invalidateTargets(queryClient, [
    { queryKey: ["admin", "areas"] },
    // Different key prefix ("areas-all-for-governorate"), so it needs its
    // own entry — otherwise the area-suggestions duplicate hint would keep
    // serving a stale area list after a create/update/delete.
    { queryKey: ["admin", "areas-all-for-governorate"] },
  ]);
}

export async function invalidateAdminAreaSuggestions(
  queryClient: QueryClient,
) {
  await invalidateTargets(queryClient, [
    { queryKey: ["admin", "area-suggestions"] },
  ]);
}

export async function invalidateAdminAppConfigAudit(
  queryClient: QueryClient,
  key: string,
) {
  await invalidateTargets(queryClient, [
    { queryKey: queryKeys.admin.appConfigAudit(key) },
  ]);
}

export async function invalidateAdminThemes(queryClient: QueryClient) {
  await invalidateTargets(queryClient, [
    { queryKey: queryKeys.admin.themes() },
  ]);
}

export async function invalidateAdminSellers(queryClient: QueryClient) {
  await invalidateTargets(queryClient, [
    { queryKey: queryKeys.admin.sellers() },
    { queryKey: queryKeys.admin.sellerOptions(), exact: true },
  ]);
}

export async function invalidateAdminStores(queryClient: QueryClient) {
  await invalidateTargets(queryClient, [
    { queryKey: queryKeys.admin.stores() },
    { queryKey: queryKeys.admin.subscriptions() },
    { queryKey: queryKeys.admin.storeSubscriptions() },
  ]);
}

export async function invalidateAdminOrders(
  queryClient: QueryClient,
  storeId: string,
) {
  await invalidateTargets(queryClient, [
    { queryKey: queryKeys.admin.storeOrders(storeId) },
  ]);
}

// A refund moves the payment transaction and the order money-state; refresh
// the transaction key + the orders list for the store.
export async function invalidateAdminOrderPayment(
  queryClient: QueryClient,
  storeId: string,
  orderId: string,
) {
  await invalidateTargets(queryClient, [
    {
      queryKey: queryKeys.admin.storeOrderPaymentTransaction(storeId, orderId),
      exact: true,
    },
    { queryKey: queryKeys.admin.storeOrders(storeId) },
  ]);
}

export async function invalidateAdminSubscriptions(queryClient: QueryClient) {
  await invalidateTargets(queryClient, [
    { queryKey: queryKeys.admin.subscriptions() },
    { queryKey: queryKeys.admin.storeSubscriptions() },
  ]);
}

export async function invalidateAdminStoreSubscriptions(
  queryClient: QueryClient,
  storeId: string,
) {
  await invalidateTargets(queryClient, [
    {
      queryKey: queryKeys.admin.storeSubscriptionAssignments(storeId),
      exact: true,
    },
    { queryKey: queryKeys.admin.storeSubscriptionPayments(storeId) },
    {
      queryKey: queryKeys.admin.storeSubscriptionCurrentSchedule(storeId),
      exact: true,
    },
    { queryKey: queryKeys.admin.storeSubscriptionLedger(storeId), exact: true },
    { queryKey: queryKeys.admin.storeSubscriptions() },
    { queryKey: queryKeys.admin.storeById(storeId), exact: true },
    { queryKey: queryKeys.admin.storeStats(storeId), exact: true },
  ]);
}
